import React from 'react';
import { useEmailSections } from '../Context/EmailSectionContext';
import DynamicComponent from './EmailModelComponents/DynamicComponent';

/**
 * Renders a preview of email template based on sections from context or directly passed data
 * Used for both in-editor preview and the dedicated preview page
 */
const EmailTemplatePreview = ({ styleSettings, sectionsData, viewMode ,templateImages }) => {
  // Get sections from context as fallback if not directly provided
  const { sections: contextSections } = useEmailSections();
  
  // Use directly passed sections if available, otherwise use context
  const modifiedSectionData = JSON.parse(JSON.stringify(sectionsData));

  modifiedSectionData.forEach((section, index) => {
    // Use section.id for unique lookup (same as save logic)
    const sectionId = section.id || `section_${index}`;
    
    // Look up main image by section id
    const match = templateImages.find(link => link.component_name === sectionId);
    if (match) {
      section.link = match.link;
      section.image = match.link;
    }
    
    // Handle Title_4Img component's multiple image fields (image1, image2, image3, image4)
    // These are saved with names like "sectionId_image1", "sectionId_image2", etc.
    ['image1', 'image2', 'image3', 'image4'].forEach(imgKey => {
      const attachmentKey = `${sectionId}_${imgKey}`;
      const imgMatch = templateImages.find(link => link.component_name === attachmentKey);
      if (imgMatch) {
        section[imgKey] = imgMatch.link;
      }
    });
    
    // Handle features array images (for components like 2COL_3ROWS_Benefits)
    if (section.features && Array.isArray(section.features)) {
      section.features.forEach((feature, featureIndex) => {
        const featureImageKey = `${sectionId}_feature_${featureIndex}_image`;
        const featureImgMatch = templateImages.find(link => link.component_name === featureImageKey);
        if (featureImgMatch) {
          feature.image = featureImgMatch.link;
          feature.image_placeholder = featureImgMatch.link;
        }
      });
    }
  });
  
  let sections = modifiedSectionData || contextSections || [];
  
  // Ensure sections is always an array
  if (!Array.isArray(sections)) {
    console.error("sections is not an array:", sections);
    
    // Try to convert from object format if possible
    if (sections && typeof sections === 'object') {
      try {
        const sectionsArray = Object.entries(sections)
          .filter(([key, value]) => value && typeof value === 'object' && value.component_name)
          .map(([key, value]) => ({ id: key, ...value }));
        
        console.log("Converted sections object to array:", sectionsArray);
        sections = sectionsArray;
      } catch (error) {
        console.error("Failed to convert sections to array:", error);
        sections = []; // Fallback to empty array to prevent errors
      }
    } else {
      sections = []; // Fallback to empty array to prevent errors
    }
  }
  
  if (!sections || sections.length === 0) {
    return (
      <div className="email-template-empty-message" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <h4>No content to preview</h4>
        <p>Add some sections to see the preview.</p>
      </div>
    );
  }

  return (
 
    <table className="email-template-preview" cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ maxWidth: '100%', margin: '0 auto' }}>
      <tbody>
        {sections.map((section, index) => {
          // Merge section-specific styleSettings with global styleSettings
          const sectionStyleSettings = section?.styleSettings || {};
          const effectiveStyleSettings = {
            ...styleSettings,  // Global styles (defaults)
            ...sectionStyleSettings  // Section-specific overrides
          };
          
          // Use section-specific backgroundColor for the row if available, otherwise use global
          const rowBackgroundColor = sectionStyleSettings.backgroundColor || styleSettings.backgroundColor || '#ffffff';
          
          return (
            <tr key={section.id || `section-${index}`} style={{ backgroundColor: rowBackgroundColor }}>
              <td className="preview-section" style={{ paddingBottom: '20px' }}>
                <DynamicComponent
                  {...section}
                  previewMode={true}
                  styleSettings={effectiveStyleSettings}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default EmailTemplatePreview; 